// src/components/ModalImageHeader.js

import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useContext
} from 'react';
import Cropper from 'react-easy-crop';
import { ModalContext } from '../context/ModalContext';
import { ModalHeaderContext } from '../context/ModalHeaderContext';
import { TabContext } from '../context/TabContext';
import './ModalImageHeader.css';

export default function ModalImageHeader({
  initialImageUrl = '',
  onCancel,
  onConfirm
}) {
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(initialImageUrl);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // ── Contexto de modales para deshabilitar header padre ─────────────
  const { modalStacks } = useContext(ModalContext);
  const { activeTab } = useContext(TabContext);
  const { setHeaderDisabled } = useContext(ModalHeaderContext);

  // Identificamos al modal padre (el que quedó justo debajo)
  const stack = modalStacks[activeTab] || [];
  const parentModal = stack[stack.length - 2];
  const parentId = parentModal?.id;

  useEffect(() => {
    if (parentId) {
      // Deshabilitar botones del header padre
      setHeaderDisabled(parentId, true);
    }
    return () => {
      if (parentId) {
        // Re-habilitar cuando se cierra este modal
        setHeaderDisabled(parentId, false);
      }
    };
  }, [parentId, setHeaderDisabled]);

  // ── Manejo de selección de archivo con límite de 5 MB ──────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
    if (file.size > MAX_SIZE) {
      setErrorMsg('El archivo supera el límite de 5 MB.');
      return;
    }
    setErrorMsg('');

    // Liberar URL anterior si no era la original
    if (previewUrl && previewUrl !== initialImageUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(file);
    const newUrl = URL.createObjectURL(file);
    setPreviewUrl(newUrl);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
  };

  // ── Callbacks de crop ───────────────────────────────────────────
  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const onMediaLoaded = useCallback((img) => {
    if (!containerRef.current) return;
    const contW = containerRef.current.clientWidth;
    const contH = (contW * 2) / 5;
    const imgAspect = img.width / img.height;
    const contAspect = 5 / 2;
    const minZoom = imgAspect > contAspect
      ? contH / img.height
      : contW / img.width;
    setZoom(minZoom);
  }, []);

  const handleZoomChange = (e) => {
    setZoom(parseFloat(e.target.value));
  };

  // ── Limpiar URL al desmontar ─────────────────────────────────────
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl !== initialImageUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl, initialImageUrl]);

  return (
    <div className="modalProfOverlay">
      <div className="modalProfCard">

        {/* Selector de imagen */}
        <div className="header-image-select">
          <button
            className="change-image-button"
            onClick={() => fileInputRef.current.click()}
          >
            Seleccionar Imagen
          </button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>

        {/* Mostrar error de tamaño */}
        {errorMsg && <p className="error-msg">{errorMsg}</p>}

        {/* Área de recorte */}
        <div className="modalProfHeaderImageEdit" ref={containerRef}>
          {previewUrl ? (
            <>
              <Cropper
                image={previewUrl}
                crop={crop}
                zoom={zoom}
                minZoom={0.2}
                maxZoom={5}
                aspect={5 / 2}
                restrictPosition={false}
                style={{ mediaStyle: { objectFit: 'contain' } }}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                onMediaLoaded={onMediaLoaded}
              />
              <div className="zoom-slider-container">
                <input
                  type="range"
                  min="0.2"
                  max="5"
                  step="0.01"
                  value={zoom}
                  onChange={handleZoomChange}
                  className="zoom-slider"
                />
              </div>
            </>
          ) : (
            <div className="placeholder">No hay imagen seleccionada</div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="modalImageFooter">
          <button className="btn-cancel" onClick={onCancel}>
            Cancelar
          </button>
          <button
            className="btn-accept"
            onClick={() =>
              onConfirm({
                file: selectedFile,
                croppedArea: croppedAreaPixels,
                zoom,
                crop
              })
            }
            disabled={!croppedAreaPixels || !!errorMsg}
          >
            Aceptar
          </button>
        </div>
      </div>
    </div>
  );
}
